import {
    DEFAULT_VM_IMAGE_NAME,
    DEFAULT_VM_INSTANCE_TYPE,
} from '@/components/tiptap-editor/tiptap-extension/zyitlab-extension/zyitlab-shared';
import { renderMarkdownToHtml } from '@/lib/prosemirror-markdown';
import { escapeXml, unescapeXml } from '@/lib/utils';
import type { ZyITLabFilePayload, ZyITLabItemPayload, ZyITLabVmImagePayload, ZyITLabVmImageTestPayload } from './zyitlab-export';

export interface ZyITLabImportPayload {
    files?: ZyITLabFilePayload[];
    items?: ZyITLabItemPayload[];
    vmImages?: ZyITLabVmImagePayload[];
    modelSolution?: string;
}

interface ZyITLabGoalGroup {
    points: number;
    message: string;
    vmImageTests: ZyITLabVmImageTestPayload[];
}

interface ZyITLabStepGroup {
    instructions: string;
    files: ZyITLabFilePayload[];
    goals: ZyITLabGoalGroup[];
}

interface ZyITLabTaskGroup {
    instructions: string;
    taskDescription: string;
    files: ZyITLabFilePayload[];
    steps: ZyITLabStepGroup[];
    isOptional: boolean;
}

function isPageBreakItem(item: ZyITLabItemPayload): item is Extract<ZyITLabItemPayload, { pageBreak: true }> {
    return 'pageBreak' in item && item.pageBreak === true;
}

function isStepItem(item: ZyITLabItemPayload): item is Extract<ZyITLabItemPayload, { step: true }> {
    return 'step' in item && item.step === true;
}

function isGoalItem(item: ZyITLabItemPayload): item is Extract<ZyITLabItemPayload, { goal: true }> {
    return 'goal' in item && item.goal === true;
}

function renderMarkdownBody(markdown: string): string {
    if (!markdown.trim()) {
        return '<p></p>';
    }
    return unescapeXml(renderMarkdownToHtml(markdown));
}

function renderZyITLabImages(files: ZyITLabFilePayload[]): string {
    return files
        .map(file => {
            const alt = file.altText ? ` alt="${escapeXml(file.altText)}"` : '';
            return `<img src="${escapeXml(file.url)}"${alt} />`;
        })
        .join('');
}

function renderZyITLabVmImageTests(tests: ZyITLabVmImageTestPayload[]): string {
    if (tests.length === 0) {
        return '<zyITLabVmImageTest run="false" command="" expected=""></zyITLabVmImageTest>';
    }
    return tests
        .map(
            test =>
                `<zyITLabVmImageTest run="${test.run ? 'true' : 'false'}" command="${escapeXml(test.command)}" expected="${escapeXml(test.expected)}"></zyITLabVmImageTest>`,
        )
        .join('');
}

function renderZyITLabStep(step: ZyITLabStepGroup): string {
    const goals = step.goals
        .map(
            goal =>
                `<zyITLabGoal points="${goal.points}" message="${escapeXml(goal.message)}">${renderZyITLabVmImageTests(goal.vmImageTests)}</zyITLabGoal>`,
        )
        .join('');
    return `<zyITLabStep><zyITLabStepInstructions>${renderMarkdownBody(step.instructions)}${renderZyITLabImages(step.files)}</zyITLabStepInstructions>${goals}</zyITLabStep>`;
}

function renderZyITLabTask(task: ZyITLabTaskGroup): string {
    const steps =
        task.steps.length > 0
            ? task.steps.map(renderZyITLabStep).join('')
            : '<zyITLabStep><zyITLabStepInstructions><p></p></zyITLabStepInstructions></zyITLabStep>';
    const taskDescription = task.taskDescription ? ` taskDescription="${escapeXml(task.taskDescription)}"` : '';
    const instructions = task.isOptional ? '<p></p>' : renderMarkdownBody(task.instructions);
    return `<zyITLabTask${taskDescription}><zyITLabTaskInstructions>${instructions}${renderZyITLabImages(task.files)}</zyITLabTaskInstructions>${steps}</zyITLabTask>`;
}

function renderZyITLabVmImages(vmImages: ZyITLabVmImagePayload[]): string {
    const images = vmImages.length > 0 ? vmImages : [{ name: DEFAULT_VM_IMAGE_NAME, title: '', instanceType: DEFAULT_VM_INSTANCE_TYPE }];
    const content = images
        .map(vm => {
            const startupCommands = vm.startupCommands ? ` startupCommands="${escapeXml(vm.startupCommands)}"` : '';
            return `<zyITLabVmImage name="${escapeXml(vm.name)}" title="${escapeXml(vm.title)}" instanceType="${escapeXml(vm.instanceType)}"${startupCommands}></zyITLabVmImage>`;
        })
        .join('');
    return `<zyITLabVmImages>${content}</zyITLabVmImages>`;
}

function createEmptyStep(): ZyITLabStepGroup {
    return { instructions: '', files: [], goals: [] };
}

function groupItemsIntoTasks(items: ZyITLabItemPayload[]): { optionalTask: ZyITLabTaskGroup | null; requiredTasks: ZyITLabTaskGroup[] } {
    const firstPageBreakIndex = items.findIndex(isPageBreakItem);
    const optionalItems = firstPageBreakIndex < 0 ? items : items.slice(0, firstPageBreakIndex);
    const requiredItems = firstPageBreakIndex < 0 ? [] : items.slice(firstPageBreakIndex);

    const optionalTask = buildTaskFromItems(optionalItems, true);
    const requiredTasks: ZyITLabTaskGroup[] = [];

    let currentTask: ZyITLabTaskGroup | null = null;
    for (const item of requiredItems) {
        if (isPageBreakItem(item)) {
            if (currentTask) {
                requiredTasks.push(currentTask);
            }
            currentTask = {
                instructions: item.instructions || '',
                taskDescription: item.taskDescription || '',
                files: item.files || [],
                steps: [],
                isOptional: false,
            };
            continue;
        }

        if (!currentTask) {
            continue;
        }

        if (isStepItem(item)) {
            currentTask.steps.push({
                instructions: item.instructions || '',
                files: item.files || [],
                goals: [],
            });
            continue;
        }

        if (isGoalItem(item)) {
            const lastStep = currentTask.steps.at(-1) ?? createEmptyStep();
            if (currentTask.steps.length === 0) {
                currentTask.steps.push(lastStep);
            }
            lastStep.goals.push({
                points: item.points,
                message: item.message || '',
                vmImageTests: item.vmImageTests || [],
            });
        }
    }

    if (currentTask) {
        requiredTasks.push(currentTask);
    }

    return { optionalTask, requiredTasks };
}

function buildTaskFromItems(items: ZyITLabItemPayload[], isOptional: boolean): ZyITLabTaskGroup | null {
    const steps: ZyITLabStepGroup[] = [];

    for (const item of items) {
        if (isStepItem(item)) {
            steps.push({
                instructions: item.instructions || '',
                files: item.files || [],
                goals: [],
            });
            continue;
        }

        if (isGoalItem(item)) {
            const lastStep = steps.at(-1) ?? createEmptyStep();
            if (steps.length === 0) {
                steps.push(lastStep);
            }
            lastStep.goals.push({
                points: item.points,
                message: item.message || '',
                vmImageTests: item.vmImageTests || [],
            });
        }
    }

    if (steps.length === 0) {
        return null;
    }

    return {
        instructions: '',
        taskDescription: '',
        files: [],
        steps,
        isOptional,
    };
}

export function buildZyITLabHtml(options: {
    guid?: string;
    caption?: string;
    instructions?: string;
    payload?: ZyITLabImportPayload;
}): string {
    const { guid = '', caption = '', instructions = '', payload = {} } = options;
    const files = payload.files || [];
    const items = payload.items || [];
    const vmImages = payload.vmImages || [];
    const modelSolution = payload.modelSolution || '';

    const { optionalTask, requiredTasks } = groupItemsIntoTasks(items);
    const tasks =
        requiredTasks.length > 0
            ? requiredTasks
            : [{ instructions: '', taskDescription: '', files: [], steps: [createEmptyStep()], isOptional: false }];

    const captionAttr = caption ? ` caption="${escapeXml(caption)}"` : '';
    const idAttr = guid ? ` id="${escapeXml(guid)}"` : '';

    let html = `<zyITLab${idAttr}${captionAttr}>`;
    html += `<zyITLabInstructions>${renderMarkdownBody(instructions)}${renderZyITLabImages(files)}</zyITLabInstructions>`;
    html += `<zyITLabModelSolution>${renderMarkdownBody(modelSolution)}</zyITLabModelSolution>`;

    if (optionalTask) {
        html += renderZyITLabTask(optionalTask);
    }

    html += renderZyITLabVmImages(vmImages);
    html += tasks.map(renderZyITLabTask).join('');
    html += '</zyITLab>';

    return html;
}
